// ═══════════════════════════════════════════════════════════════════════════════
//  Arch Quick Calculator — pure calculation helpers
//  No React, no hooks, no side effects.  Just math.
// ═══════════════════════════════════════════════════════════════════════════════

import {
    SCOPE_OPTION_TO_PCT,
    DISCIPLINE_SCOPE_MULTIPLIERS,
    DEFAULT_BASE_PRICES,
    ADD_ALT_BASE_PRICES,
    ADD_ALT_MIN_FLOORS,
    ADD_ALT_DIFFICULTY_DEFAULTS,
} from './archConfig'

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100

// ── Discipline breakdown from scope key ──────────────────────────────────────
export function getScopePercents(scopeKey, disciplinePresets) {
    const source = disciplinePresets || SCOPE_OPTION_TO_PCT
    return (
        source[scopeKey] || {
            archPct: 50,
            structPct: 22,
            mepPct: 20,
            civilPct: 0,
            adminPct: 8,
        }
    )
}

// ── Scope multiplier for a given market + scope ─────────────────────────────
export function getScopeMultiplier(market, scopeKey, scopeMultipliers) {
    const source = scopeMultipliers || DISCIPLINE_SCOPE_MULTIPLIERS
    return source[market]?.[scopeKey] ?? 1
}

// ── Base price per sqft for a market ─────────────────────────────────────────
export function getBasePrice(market, customBasePrice) {
    if (customBasePrice != null) return Number(customBasePrice) || 0
    return DEFAULT_BASE_PRICES[market] ?? 0
}

// ── Add/Alt base rate for a market ───────────────────────────────────────────
export function getAddAltBaseRate(market, customRate) {
    if (customRate != null) return Number(customRate) || 0
    return ADD_ALT_BASE_PRICES[market] ?? 0
}

// ═══════════════════════════════════════════════════════════════════════════════
//  1. NEW CONSTRUCTION
// ═══════════════════════════════════════════════════════════════════════════════
export function calcNewConstruction({ market, sqft, difficulty, disciplineScope, customBasePrice, percents: customPercents, scopeMultipliers, disciplinePresets }) {
    const basePrice = getBasePrice(market, customBasePrice)
    const scopeMult = getScopeMultiplier(market, disciplineScope, scopeMultipliers)
    const rawSqft = Number(sqft) || 0
    const diff = Number(difficulty) || 1

    const salePrice = round2(rawSqft * basePrice * diff * scopeMult)
    const percents = customPercents || getScopePercents(disciplineScope, disciplinePresets)

    const archAmt = round2(salePrice * ((percents.archPct || 0) / 100))
    const structAmt = round2(salePrice * ((percents.structPct || 0) / 100))
    const mepAmt = round2(salePrice * ((percents.mepPct || 0) / 100))
    const civilAmt = round2(salePrice * ((percents.civilPct || 0) / 100))
    const adminAmt = round2(salePrice * ((percents.adminPct || 0) / 100))

    return {
        sqft: rawSqft,
        effectiveSqft: rawSqft,
        basePrice,
        difficulty: diff,
        scopeMult,
        salePrice,
        archAmt,
        structAmt,
        mepAmt,
        civilAmt,
        adminAmt,
        totalContract: salePrice,
        percents,
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  2. ADD / REM / ALT
// ═══════════════════════════════════════════════════════════════════════════════
export function calcAddRem({
    market,
    existSqft,
    existDiff,
    remSqft,
    remDiff,
    addSqft,
    addDiff,
    disciplineScope,
    customExistRate,
    customRemRate,
    customAddRate,
    percents: customPercents,
    minFloors,
    scopeMultipliers,
    disciplinePresets,
}) {
    const baseRate = getAddAltBaseRate(market, null) // overall fallback
    const existBaseRate = customExistRate != null ? Number(customExistRate) : baseRate
    const remBaseRate = customRemRate != null ? Number(customRemRate) : baseRate
    const addBaseRate = customAddRate != null ? Number(customAddRate) : baseRate

    const eSqft = Number(existSqft) || 0
    const rSqft = Number(remSqft) || 0
    const aSqft = Number(addSqft) || 0

    const eDiff = Number(existDiff) || 0
    const rDiff = Number(remDiff) || 0
    const aDiff = Number(addDiff) || 0

    const existEff = round2(eSqft * eDiff)
    const remEff = round2(rSqft * rDiff)
    const addEff = round2(aSqft * aDiff)

    const effectiveSqft = round2(existEff + remEff + addEff)
    const computedAddAlt = round2(existEff * existBaseRate + remEff * remBaseRate + addEff * addBaseRate)

    const floors = minFloors || ADD_ALT_MIN_FLOORS
    const activeMin =
        (eSqft > 0 ? floors.exist : 0) +
        (rSqft > 0 ? floors.remodel : 0) +
        (aSqft > 0 ? floors.adicion : 0)

    const salePrice = round2(Math.max(computedAddAlt, activeMin))
    const percents = customPercents || getScopePercents(disciplineScope, disciplinePresets)

    const archAmt = round2(salePrice * ((percents.archPct || 0) / 100))
    const structAmt = round2(salePrice * ((percents.structPct || 0) / 100))
    const mepAmt = round2(salePrice * ((percents.mepPct || 0) / 100))
    const civilAmt = round2(salePrice * ((percents.civilPct || 0) / 100))
    const adminAmt = round2(salePrice * ((percents.adminPct || 0) / 100))

    return {
        existSqft: eSqft,
        remSqft: rSqft,
        addSqft: aSqft,
        existEff,
        remEff,
        addEff,
        effectiveSqft,
        existBaseRate,
        remBaseRate,
        addBaseRate,
        computedAddAlt,
        activeMin,
        salePrice,
        archAmt,
        structAmt,
        mepAmt,
        civilAmt,
        adminAmt,
        totalContract: salePrice,
        percents,
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  3. CIVIL (with split: main + civil)
// ═══════════════════════════════════════════════════════════════════════════════
export function calcCivilSplit({
    mainMarket,
    mainSqft,
    mainDifficulty,
    civilSqft,
    civilDifficulty,
    disciplineScope,
    customMainBasePrice,
    customCivilBasePrice,
    percents: customPercents,
    scopeMultipliers,
    disciplinePresets,
}) {
    // Derive civil market from main market
    const state = mainMarket.startsWith('TX') ? 'TX' : 'FL'
    const civilMarket = `${state}_CIVIL`

    const mainBasePrice = getBasePrice(mainMarket, customMainBasePrice)
    const civilBasePrice = getBasePrice(civilMarket, customCivilBasePrice)

    const mSqft = Number(mainSqft) || 0
    const cSqft = Number(civilSqft) != null ? Number(civilSqft) : mSqft
    const mDiff = Number(mainDifficulty) || 1
    const cDiff = Number(civilDifficulty) != null ? Number(civilDifficulty) : mDiff

    const mainScopeMult = getScopeMultiplier(mainMarket, disciplineScope, scopeMultipliers)
    const civilScopeMult = getScopeMultiplier(civilMarket, disciplineScope, scopeMultipliers)

    const mainPrice = round2(mSqft * mainBasePrice * mDiff * mainScopeMult)
    const civilPrice = round2(cSqft * civilBasePrice * cDiff * civilScopeMult)
    const salePrice = round2(mainPrice + civilPrice)

    const percents = customPercents || getScopePercents(disciplineScope, disciplinePresets)

    // Main pool: arch/struct/mep/admin percentages apply to mainPrice only
    const archAmt = round2(mainPrice * ((percents.archPct || 0) / 100))
    const structAmt = round2(mainPrice * ((percents.structPct || 0) / 100))
    const mepAmt = round2(mainPrice * ((percents.mepPct || 0) / 100))
    const adminAmt = round2(mainPrice * ((percents.adminPct || 0) / 100))

    // Civil pool: split by civilPct (engineering) and civilAdminPct (coordination)
    const civilAdminPct = percents.civilAdminPct ?? 20
    const civilAmt = round2(civilPrice * ((100 - civilAdminPct) / 100))
    const civilAdminAmt = round2(civilPrice * (civilAdminPct / 100))

    return {
        mainMarket,
        civilMarket,
        mainSqft: mSqft,
        civilSqft: cSqft,
        mainDifficulty: mDiff,
        civilDifficulty: cDiff,
        mainBasePrice,
        civilBasePrice,
        mainScopeMult,
        civilScopeMult,
        mainPrice,
        civilPrice,
        salePrice,
        archAmt,
        structAmt,
        mepAmt,
        civilAmt,
        adminAmt,
        civilAdminAmt,
        totalContract: salePrice,
        percents,
    }
}
